/**
 * The frame around the sign-in and sign-up forms.
 *
 * Two columns on a wide screen: the product's promise on the left, the form on the right. The
 * left panel is painted with `--accent` and `--text-on-accent`, so a white-label tenant's colours
 * carry through to the first screen their users see — and the panel drops away below `lg`, where
 * a phone's width belongs to the form.
 *
 * The four sentences on the panel are product-strategy.md's positioning in the user's words, not
 * marketing superlatives: each is something the product does or promises, so the same sentence
 * can be read back to us in a complaint.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Icon, Wordmark, type IconName } from '@flowza/ui'

/**
 * Every line here is checked against what the product DOES on the day it is written, because this
 * copy is the first thing a customer reads and the last thing anybody re-reads. retarget-go-live.md
 * §6 found four untrue sentences on this panel; two became true when the send path and the chain
 * writer landed, and two were replaced:
 *
 *   - "Your documents stay in the region you choose" — one cell, no choosing (§5, the residency
 *     debt). Replaced with what every party actually gets at completion.
 *   - "…and the API" — API keys are deferred (§4). The console is for sending and administration.
 *
 * "Court-ready" is not claimed. The chain is tamper-evident and verifiable; what a court makes of
 * it is counsel's sentence to write, not ours.
 */
const PROMISES: ReadonlyArray<{ readonly icon: IconName; readonly text: string }> = [
  { icon: 'send', text: 'Send a document for signature in minutes.' },
  {
    icon: 'shield',
    text: 'A tamper-evident, hash-chained audit trail on every envelope, on every plan.',
  },
  {
    icon: 'globe',
    text: 'Every party receives the signed document and its fingerprint when it completes.',
  },
  { icon: 'code', text: 'One console for sending and administration.' },
]

export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  readonly title: string
  readonly subtitle?: ReactNode
  readonly children: ReactNode
  /** The way onward: the other form, a reset link. Every auth screen has one (ux.md §12). */
  readonly footer?: ReactNode
}) {
  return (
    <div className="bg-surface-base min-h-screen lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      <aside className="bg-accent text-text-on-accent relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,var(--accent-hover),transparent_65%)]"
        />
        <div
          aria-hidden="true"
          className="bg-text-on-accent/10 pointer-events-none absolute -bottom-24 -right-24 size-96 rounded-full"
        />
        <div className="relative">
          {/*
            `lg`, and STACKED — the lockup the logo pack supplies, mark above name. This is the
            only place in the product that draws the mark's FULL form: at `lg`'s 42 px the second
            wave, the pen's seam and tip, and the signature under its nib all render, where the
            sidebar's 30 px draws the simplified one. The size is the founder's (brand.tsx,
            `WORDMARK_SIZE`): 40% of the 104 px this was, because a mark as wide as the wordmark
            under it made the panel read as a brand plate rather than as a sign-in page. (ADR
            0054's amendment had already moved this from `md` to `lg`, because at `md` the full
            drawing existed in the design system and appeared nowhere in the product; ADR 0089
            stacks it, and its second amendment sets the size.)
          */}
          <Wordmark size="lg" tone="on-accent" layout="stacked" />
        </div>
        <div className="relative max-w-md">
          <h2 className="m-0 text-3xl font-semibold leading-tight tracking-tight">
            Agreements signed in minutes. Evidence that lasts for years.
          </h2>
          <ul className="m-0 mt-8 flex list-none flex-col gap-4 p-0">
            {PROMISES.map((promise) => (
              <li key={promise.icon} className="flex items-start gap-3">
                <span className="bg-text-on-accent/15 flex size-7 shrink-0 items-center justify-center rounded-md">
                  <Icon name={promise.icon} size={16} />
                </span>
                <span className="text-base leading-7 opacity-90">{promise.text}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative m-0 text-sm opacity-75">
          Built for teams who sign things every day.
        </p>
      </aside>

      <main className="flex flex-col px-4 py-8 sm:px-8 lg:min-h-screen lg:justify-center lg:px-16">
        <div className="mx-auto mb-8 w-full max-w-md lg:hidden">
          <Link to="/" className="inline-flex rounded-md" aria-label="FlowZa Sign">
            <Wordmark size="sm" />
          </Link>
        </div>
        <div className="mx-auto w-full max-w-md">
          <h1 className="text-text-primary m-0 text-2xl font-semibold tracking-tight">{title}</h1>
          {subtitle ? <p className="text-text-muted mb-6 mt-1">{subtitle}</p> : null}
          {children}
          {footer ? <p className="text-text-muted mt-6 text-sm">{footer}</p> : null}
        </div>
      </main>
    </div>
  )
}
